// client/src/components/reputation/ReputationPanel.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, CircularProgress } from '@mui/material';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import BadgeDisplay from './BadgeDisplay';

const TRUST_LEVELS = [
  { level: 0, name: 'New Member',  minRP: 0    },
  { level: 1, name: 'Member',      minRP: 25   },
  { level: 2, name: 'Contributor', minRP: 100  },
  { level: 3, name: 'Trusted',     minRP: 300  },
  { level: 4, name: 'Curator',     minRP: 750  },
  { level: 5, name: 'Veteran',     minRP: 2000 },
];

/**
 * RP total, trust level, progress bar, and recent log on a user profile.
 * Props: userId
 */
export default function ReputationPanel({ userId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFeatureEnabled('enableReputationSystem') || !userId) { setLoading(false); return; }
    axiosInstance.get(`/reputation/${userId}`)
      .then(res => setRep(res.data))
      .catch(() => setRep(null))
      .finally(() => setLoading(false));
  }, [userId, isFeatureEnabled]);

  if (!isFeatureEnabled('enableReputationSystem')) return null;
  if (loading) return <CircularProgress size={20} />;
  if (!rep) return null;

  const current = TRUST_LEVELS.find(t => t.level === rep.trustLevel);
  const next = TRUST_LEVELS.find(t => t.level === rep.trustLevel + 1);
  const progress = next
    ? Math.min(100, ((rep.reputationPoints - current.minRP) / (next.minRP - current.minRP)) * 100)
    : 100;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        <strong>{rep.reputationPoints} RP</strong> · {rep.trustLevelName}
      </Typography>
      {next && (
        <Box sx={{ mt: 0.5 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          <Typography variant="caption" color="text.secondary">
            {next.minRP - rep.reputationPoints} RP to {next.name}
          </Typography>
        </Box>
      )}
      <BadgeDisplay userId={userId} />
    </Box>
  );
}
